import clsx from "clsx";
import { GridLoader } from "react-spinners";
import { Banner } from "../../../../core/models/db";
import { useIdentityStore, useSnackbarStore } from "../../../../core/store";
import { UploadFile } from "../../../components/drag-drop/model/UploadFile";
import { FileService } from "../../../components/drag-drop/services/file.service";
import { CContainer } from "@coreui/react-pro";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderContext } from "../../../components/loader/context/LoaderContext";
import { useTranslation } from "react-i18next";
import { HiArrowNarrowLeft } from "react-icons/hi";
import { BASE_ROUTE, ROUTE_NAME_ID_2, RouteName } from "../../../../core/constants/route_name";
import { Util } from "../../../../core/util/util";
import BannerForm from "./BannerForm";
import { useBanner } from "../../../../core/hooks/db/fisioteduca/useFisioteduca";
import * as MessageSnackbarDefault from "../../../../core/constants/message-snackbar/msgFormDefault";

const BannerDetail = () => {
  const { t } = useTranslation();
  const { identity } = useIdentityStore((state) => state);
  const navigate = useNavigate();

  const [banner, setBanner] = useState<Banner | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [typeAction, setTypeAction] = useState<"add" | "update">(null);
  const { addSnackbar, setMaxSnackbars } = useSnackbarStore();


  const { getBannerById, addBanner, updateBanner } = useBanner();
  const { id } = useParams();

  useEffect(() => {
    if (formRef.current) {
      // Just reading the height if needed
      console.log("Form height:", formRef.current.clientHeight);
    }
  }, [loading, banner]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (id) {
        // Update
        const result = await getBannerById(id);
        if (result && result.ok && result.item) {
          setBanner(result.item);
          setTypeAction("update");
        } else {
          console.warn("No se encontró Banner con id", id);
          const newRoute = ROUTE_NAME_ID_2({}, {}, RouteName.banner);
          navigate(`${BASE_ROUTE}${newRoute}`);
        }
      } else {
        // Add
        setBanner({} as Banner);
        setTypeAction("add");
      }
      setLoading(false);
    })();
  }, [id]);

  const createFile = async (file: File, bannerId: string) => {
    try {
      const { data } = await FileService.add(new UploadFile(file, ["images", "banner", bannerId]), null);
      if (data.ok) {
        return `/${data.item[0].destination}/${data.item[0].filename}`;
      }
      return undefined;
    } catch (err) {
      throw err;
    }
  };

  // const handleAdd = async (
  //   banner: Banner,
  //   imageItem: { oldImage: string; image: string; nameImage: string }
  // ) => {
  //   try {
  //     setLoading(true);

  //     if (imageItem.image && imageItem.image.startsWith("blob")) {
  //       const file = await Util.transformBlobToFile(
  //         imageItem.image,
  //         banner.title + "_" + Date.now() + ".png"
  //       );
  //       banner.image = await createFile(file, banner.id || "temp-id");
  //     }

  //     const res = await addBanner(banner);
  //     if (res.ok) {
  //       setTypeAction("update");
  //       setBanner(res.item);
  //     }
  //   } catch (err) {
  //     console.error("Error al crear Banner", err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const handleAdd = async (
    banner: Banner,
    imageItem: { oldImage: string; image: string; nameImage: string }
  ) => {
    try {
      setLoading(true);

      // 1. Crear una copia del Banner sin la imagen
      const bannerToCreate = { ...banner };
      delete bannerToCreate.image;

      // 2. Crear el Banner sin la imagen para obtener el ID
      const createResult = await addBanner(bannerToCreate);
      if (!createResult.ok) {
        throw new Error("Error al crear el Banner");
      }

      const createdBanner = createResult.item;
      setTypeAction("update");
      setBanner(createdBanner);

      const newBannerId = createdBanner.id;

      // 3. Subir la imagen principal si existe y es un blob
      let mainImagePath = createdBanner.image;
      if (imageItem.image && imageItem.image.startsWith("blob:")) {
        const fileName = `${banner.title}_${Date.now()}.png`;
        const file = await Util.transformBlobToFile(imageItem.image, fileName);
        mainImagePath = await createFile(file, newBannerId);

        if (mainImagePath) {
          // 4. Actualizar el Banner con la ruta de la imagen
          const updatedBanner = {
            ...createdBanner,
            image: mainImagePath,
          };

          const updateResult = await updateBanner(updatedBanner);
          if (updateResult.ok) {
            setBanner(updateResult.item);

          } else {
            throw new Error("Error al actualizar el Banner con la imagen");
          }

        }
      }

      addSnackbar(MessageSnackbarDefault.successAddDefault);
    } catch (err) {
      console.error("Error al crear Banner", err);
      addSnackbar(MessageSnackbarDefault.errorDefault);
      // Aquí podrías implementar notificaciones al usuario sobre el error
    } finally {
      setLoading(false);
    }
  };


  const handleUpdate = async (
    banner: Banner,
    imageItem: { oldImage: string; image: string; nameImage: string }
  ) => {
    try {
      setLoading(true);

      if (imageItem.image && imageItem.image.startsWith("blob")) {
        const file = await Util.transformBlobToFile(
          imageItem.image,
          banner.title + "_" + Date.now() + ".png"
        );
        banner.image = await createFile(file, banner.id);
        if (imageItem.oldImage && imageItem.oldImage !== banner.image) {
          await FileService.delete(imageItem.oldImage);
        }
      }

      const res = await updateBanner(banner);
      if (res.ok) {
        setBanner(res.item);
        addSnackbar(MessageSnackbarDefault.successEditDefault);
      }
    } catch (err) {
      console.error("Error al actualizar Banner", err);
      addSnackbar(MessageSnackbarDefault.errorDefault);
    } finally {
      setLoading(false);
    }
  };

  const handleRecord = (
    val: Banner,
    imageItem: { oldImage: string; image: string; nameImage: string }
  ) => {
    if (typeAction === "add") {
      handleAdd(val, imageItem);
    } else if (typeAction === "update") {
      handleUpdate(val, imageItem);
    }
  };

  return (
    <CContainer style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-full justify-between items-center flex pt-4 pb-5">
        <div className="flex flex-col">
          <div
            className="flex items-center hover:underline cursor-pointer mb-5"
            onClick={() => {
              const pagesRoutes = t("routes", { returnObjects: true });
              let newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.banner);
              navigate(`${BASE_ROUTE}${newRoute}`);
            }}
          >
            <HiArrowNarrowLeft size={20} className="mr-1" />
            {"Volver a Banners"}
          </div>

          <span className="text-4xl" style={{ fontWeight: 500 }}>
            {"Banners"}
          </span>
          <span className="small text-medium-emphasis">
            {typeAction === "add" ? "Crear Banner" : "Detalle Banner"}
          </span>
        </div>
      </div>

      <div className="w-full flex justify-center mb-10">
        <div className="w-full flex justify-center gap-4 flex-col xl:flex-row ">
          <div
            className={clsx(`flex flex-col w-full justify-center items-center`, {
              "xl:w-[100%]": banner?.id,
            })}
          >
            <div className="bg-white block p-4 md:p-7 shadow-md md:rounded-lg w-full md:w-[70%]">
              <div className="text-2xl font-medium mb-4">Información del Banner</div>
              <div className="w-full flex flex-col md:flex-row justify-center md:gap-4">
                {loading && (
                  <div
                    className="w-full flex flex-col justify-center items-center"
                    style={{ height: 547 }}
                  >
                    <GridLoader color="#3d4f65" size={25} speedMultiplier={0.7} />
                  </div>
                )}

                {!loading && banner && (
                  <div ref={formRef} className="p-0">
                    <BannerForm
                      initialBanner={banner}
                      onSubmit={handleRecord}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CContainer>
  );
};

export default BannerDetail;
